import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { hideCurrentModal, showModal } from '../globalState';
import { useIsModalActive } from './utilsApp';
import Screen from './Screen';
import Button from './Button';
import Input from './Input';
import PixelartIcon, { pixelartIcons } from './PixelartIcon';
import {
  accountState,
  localProfile,
  createAccount,
  signIn,
  signOutAccount,
  setAccountSkin,
  getActiveSkinId,
  isAccountsAvailable,
} from '../account';
import { builtinSkins } from '../skinsRegistry';
import styles from './AccountModal.module.css';

const SkinPicker = () => {
  const activeSkinId = getActiveSkinId();
  return (
    <div className={styles.skinGrid}>
      {builtinSkins.map((skin) => (
        <div
          key={skin.id}
          className={`${styles.skinOption} ${skin.id === activeSkinId ? styles.selected : ''}`}
          onClick={() => void setAccountSkin(skin.id)}
        >
          <img src={skin.url} alt={skin.name} width={32} height={32} style={{ imageRendering: 'pixelated' }} />
          <span className={styles.skinName}>{skin.name}</span>
        </div>
      ))}
    </div>
  );
};

const SignedInPanel = () => {
  const snap = useSnapshot(accountState);
  return (
    <>
      <div className={styles.profileHeader}>
        <img
          src={builtinSkins.find((s) => s.id === (snap.skinId ?? 'steve'))?.url}
          alt=""
          width={28}
          height={28}
          style={{ imageRendering: 'pixelated' }}
        />
        <span className={styles.username}>{snap.username ?? '...'}</span>
      </div>
      <SkinPicker />
      <div className={styles.actions}>
        <Button onClick={() => void signOutAccount()}>Sign out</Button>
        <Button onClick={() => hideCurrentModal()}>Done</Button>
      </div>
    </>
  );
};

const SignInForm = ({ onSwitchToCreate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      hideCurrentModal();
    } catch (err) {
      setError(err instanceof Error ? err.message.replace('Firebase: ', '') : 'Failed to sign in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label}>Email</label>
        <Input width={280} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className={styles.field}>
        <label className={styles.label}>Password</label>
        <Input width={280} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <div className={styles.error}>{error}</div>
      <div className={styles.actions}>
        <Button disabled={loading || !email || !password} onClick={() => void submit()}>
          {loading ? 'Signing in...' : 'Sign in'}
        </Button>
      </div>
      <div className={styles.switchRow}>
        No account? <span className={styles.link} onClick={onSwitchToCreate}>Create one</span>
      </div>
    </>
  );
};

const CreateAccountForm = ({ onSwitchToSignIn }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await createAccount(email, password, username);
      hideCurrentModal();
    } catch (err) {
      setError(err instanceof Error ? err.message.replace('Firebase: ', '') : 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label}>Username (permanent)</label>
        <Input width={280} value={username} onChange={(e) => setUsername(e.target.value)} maxLength={16} />
      </div>
      <div className={styles.field}>
        <label className={styles.label}>Email</label>
        <Input width={280} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className={styles.field}>
        <label className={styles.label}>Password</label>
        <Input width={280} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <div className={styles.error}>{error}</div>
      <div className={styles.actions}>
        <Button disabled={loading || !email || !password || !username} onClick={() => void submit()}>
          {loading ? 'Creating...' : 'Create account'}
        </Button>
      </div>
      <div className={styles.switchRow}>
        Already have one? <span className={styles.link} onClick={onSwitchToSignIn}>Sign in</span>
      </div>
    </>
  );
};

const LoggedOutPanel = () => {
  const [mode, setMode] = useState('signin');
  return (
    <>
      {mode === 'signin' ? (
        <SignInForm onSwitchToCreate={() => setMode('create')} />
      ) : (
        <CreateAccountForm onSwitchToSignIn={() => setMode('signin')} />
      )}
      <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 10 }}>
        <label className={styles.label}>Playing without an account? Pick a skin:</label>
        <SkinPicker />
        <Button onClick={() => hideCurrentModal()}>Continue without an account</Button>
      </div>
    </>
  );
};

export default () => {
  const isModalActive = useIsModalActive('account');
  const snap = useSnapshot(accountState);
  if (!isModalActive) return null;
  return (
    <Screen title="" backdrop className={styles.modalScreen}>
      <div className={styles.container}>
        <button className={styles.closeButton} onClick={() => hideCurrentModal()} aria-label="Close" type="button">
          <PixelartIcon iconName={pixelartIcons.close} width={12} />
        </button>
        <h2 className={styles.title}>{snap.status === 'signed-in' ? 'Your Profile' : 'Account'}</h2>
        {!isAccountsAvailable() ? (
          <>
            <div className={styles.error} style={{ color: '#ccc' }}>Accounts are not configured for this build.</div>
            <label className={styles.label}>Pick a skin:</label>
            <SkinPicker />
            <Button onClick={() => hideCurrentModal()}>Done</Button>
          </>
        ) : snap.status === 'signed-in' ? (
          <SignedInPanel />
        ) : (
          <LoggedOutPanel />
        )}
      </div>
    </Screen>
  );
};

export const openAccountModal = () => showModal({ reactType: 'account' });

export const AccountNameplate = () => {
  const snap = useSnapshot(accountState);
  const label = snap.status === 'signed-in' ? snap.username ?? '...' : 'Account';
  return (
    <div className={styles.nameplate} onClick={openAccountModal} data-test-id="account-nameplate">
      <PixelartIcon iconName={pixelartIcons.folder} width={10} />
      <span>{label}</span>
    </div>
  );
};
